import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  TextField,
  Button,
  Container,
  Fade,
  Grow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon, Close as CloseIcon } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import PixiWorldViewer from '../components/PixiWorldViewer';
import { useLocalization } from '../hooks/useLocalization';

const UserAdminPanel = () => {
  const { t } = useLocalization();
  const [worldConfig, setWorldConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserLoading, setNewUserLoading] = useState(false);
  const [newUserError, setNewUserError] = useState('');
  const [newUserSuccess, setNewUserSuccess] = useState('');
  const { token } = useAuth();
  const worldViewerRef = useRef(null);
  const [backToSearchLoading, setBackToSearchLoading] = useState(false);

  const fetchWorldForUser = async (email) => {
    try {
      setLoading(true);
      setError('');
      
      // First, check client settings to see if we need to add attachments
      let clientSettings = null;
      try {
        const settingsResponse = await axios.get('/client-settings/', {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        clientSettings = settingsResponse.data;
      } catch (settingsErr) {
        console.warn('Could not fetch client settings:', settingsErr);
      }
      
      // If add_attachment_for_each_view is enabled, add a new attachment first
      if (clientSettings && clientSettings.add_attachment_for_each_view) {
        try {
          await axios.post('/attachments/add', 
            { user_email: email },
            {
              headers: { 
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          console.log('Added new attachment for user:', email);
        } catch (attachmentErr) {
          console.warn('Could not add attachment for user:', attachmentErr);
        }
      }
      
      const response = await axios.post(`/pixi-world/world/by-email/${encodeURIComponent(email)}`, {}, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setWorldConfig(response.data);
      setHasSearched(true);
      
      // Enter fullscreen mode when game world is displayed
      setTimeout(() => {
        if (!document.fullscreenElement) {
          document.documentElement.requestFullscreen().catch(err => {
            console.log('Fullscreen request failed:', err);
          });
        }
      }, 500); // Small delay to ensure the game world is rendered
      
    } catch (err) {
      console.error('Error generating world for user:', err);
      setError(t('failed_to_load_world'));
      setWorldConfig(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!userEmail.trim()) {
      setError(t('please_enter_email'));
      return;
    }
    
    // Enter fullscreen mode immediately when user clicks search
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(err => {
        console.log('Fullscreen request failed:', err);
      });
    }
    
    fetchWorldForUser(userEmail.trim());
  };

  const handleBackToSearch = async () => {
    // Prevent multiple clicks
    if (backToSearchLoading) {
      return;
    }

    try {
      setBackToSearchLoading(true);
      
      // Persist any pending score updates before going back
      if (worldViewerRef.current && worldViewerRef.current.persistScoreUpdates) {
        await worldViewerRef.current.persistScoreUpdates();
      }
      
      setWorldConfig(null);
      setHasSearched(false);
      setError('');
    } catch (error) {
      console.error('Error going back to search:', error);
    } finally {
      setBackToSearchLoading(false);
    }
  };

  const handleBeforeClose = async () => {
    // Persist any pending score updates before closing
    if (worldViewerRef.current && worldViewerRef.current.persistScoreUpdates) {
      await worldViewerRef.current.persistScoreUpdates();
    }
  };

  const handleNewUserClick = () => {
    setNewUserDialogOpen(true);
    setNewUserEmail('');
    setNewUserError('');
    setNewUserSuccess('');
  };

  const handleNewUserClose = () => {
    setNewUserDialogOpen(false);
    setNewUserEmail('');
    setNewUserError('');
    setNewUserSuccess('');
  };

  const handleNewUserSubmit = async (e) => {
    e.preventDefault();
    if (!newUserEmail.trim()) {
      setNewUserError(t('please_enter_valid_email'));
      return;
    }

    try {
      setNewUserLoading(true);
      setNewUserError('');
      setNewUserSuccess('');

      const response = await axios.post('/users/register', 
        { email: newUserEmail.trim() },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setNewUserSuccess(t('user_registered_successfully', `User ${newUserEmail.trim()} registered successfully!`).replace('{email}', newUserEmail.trim()));
      
      // Close the dialog
      handleNewUserClose();
      
      // Enter fullscreen mode immediately when registering new user
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen().catch(err => {
          console.log('Fullscreen request failed:', err);
        });
      }
      
      // Automatically show the game world for the newly registered user
      setUserEmail(newUserEmail.trim());
      fetchWorldForUser(newUserEmail.trim());
      
    } catch (err) {
      console.error('Error registering new user:', err);
      setNewUserError(err.response?.data?.detail || t('failed_to_register'));
    } finally {
      setNewUserLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      bgcolor: 'background.default',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {!hasSearched || !worldConfig ? (
        // Email Entry Page
        <Container 
          maxWidth="md" 
          sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            py: 4
          }}
        >
          <Fade in={true} timeout={800}>
            <Paper
              elevation={8}
              sx={{
                p: 6,
                width: '100%',
                maxWidth: 650,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                textAlign: 'center',
                position: 'relative',
                overflow: 'hidden',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  zIndex: 0
                }
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography 
                  variant="h3" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold',
                    mb: 1,
                    textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
                  }}
                >
                  {t('game_world_viewer')}
                </Typography>
                
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 4, 
                    opacity: 0.9,
                    fontWeight: 300
                  }}
                >
                  {t('enter_user_email')}
                </Typography>

                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      mb: 3, 
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                      color: 'error.main'
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <Box 
                  component="form" 
                  onSubmit={handleSearch} 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 3,
                    width: '100%'
                  }}
                >
                  <TextField
                    type="email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    fullWidth
                    placeholder={t('enter_user_email')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                        borderRadius: 2,
                        color: 'black',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.95)',
                        },
                        '&.Mui-focused': {
                          bgcolor: 'rgba(255, 255, 255, 0.95)',
                        },
                        '& input': {
                          color: 'black !important',
                        },
                        '& input::placeholder': {
                          color: 'rgba(0, 0, 0, 0.6) !important',
                        },
                        '& .MuiInputBase-input': {
                          color: 'black !important',
                        }
                      },
                      '& .MuiInputLabel-root': {
                        color: 'black',
                        '&.Mui-focused': {
                          color: 'black'
                        }
                      },
                      '& .MuiInputBase-input': {
                        color: 'black !important',
                      }
                    }}
                  />
                  
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 2, 
                    flexDirection: 'column',
                    width: '100%'
                  }}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading || !userEmail.trim()}
                      startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                      sx={{
                        py: 1.5,
                        px: { xs: 4, sm: 6 },
                        borderRadius: 2,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        fontWeight: 'bold',
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.3)',
                        minWidth: { xs: '100%', sm: '240px' },
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.3)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
                        },
                        '&:disabled': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          color: 'rgba(255, 255, 255, 0.5)'
                        },
                        transition: 'all 0.3s ease',
                        flex: 1
                      }}
                    >
                      {loading ? t('loading') : t('explore_game_world')}
                    </Button>

                    <Button
                      variant="outlined"
                      size="large"
                      onClick={handleNewUserClick}
                      startIcon={<AddIcon />}
                      sx={{
                        py: 1.5,
                        px: { xs: 4, sm: 5 },
                        borderRadius: 2,
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        fontWeight: 'bold',
                        color: 'white',
                        borderColor: 'rgba(255, 255, 255, 0.5)',
                        minWidth: { xs: '100%', sm: '180px' },
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
                        },
                        transition: 'all 0.3s ease',
                        flex: 1
                      }}
                    >
                      {t('new_user')}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Fade>
        </Container>
      ) : (
        // Full Page Game World Viewer
        <Box sx={{ 
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1000,
          bgcolor: 'black'
        }}>
          {/* Full page game world viewer */}
          <PixiWorldViewer 
            ref={worldViewerRef}
            worldConfig={worldConfig} 
            onClose={handleBackToSearch}
            onRefresh={() => fetchWorldForUser(userEmail)}
            onBeforeClose={handleBeforeClose}
          />
        </Box>
      )}

      {/* New User Dialog */}
      <Dialog 
        open={newUserDialogOpen} 
        onClose={handleNewUserClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {t('register_new_user')}
          </Typography>
          <IconButton
            onClick={handleNewUserClose}
            sx={{ color: 'white' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            {t('enter_email_address')}
          </Typography>

          {newUserError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {newUserError}
            </Alert>
          )}

          {newUserSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              {newUserSuccess}
            </Alert>
          )}

          <Box component="form" onSubmit={handleNewUserSubmit}>
            <TextField
              type="email"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              fullWidth
              placeholder={t('enter_email_address')}
              disabled={newUserLoading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: 2,
                  color: 'black',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                  },
                  '&.Mui-focused': {
                    bgcolor: 'rgba(255, 255, 255, 0.95)',
                  },
                  '& input': {
                    color: 'black !important',
                  },
                  '& input::placeholder': {
                    color: 'rgba(0, 0, 0, 0.6) !important',
                  },
                  '& .MuiInputBase-input': {
                    color: 'black !important',
                  }
                },
                '& .MuiInputLabel-root': {
                  color: 'black',
                  '&.Mui-focused': {
                    color: 'black'
                  }
                },
                '& .MuiInputBase-input': {
                  color: 'black !important',
                }
              }}
            />
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button
            onClick={handleNewUserClose}
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleNewUserSubmit}
            variant="contained"
            disabled={newUserLoading || !newUserEmail.trim()}
            startIcon={newUserLoading ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.3)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
              },
              '&:disabled': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.5)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {newUserLoading ? t('registering') : t('register_user')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserAdminPanel; 